import { useTranslation } from 'react-i18next';

import type { UserAdminRequestState } from 'src/types/user';
import toSnakeCase from 'src/helper/toSnakeCase';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpServiceInstance from 'src/services/api/http';
import { useUserAdmin } from 'src/components/userAdmin/UserAdminContext';
import type { BulkEditDialogValidatedValues } from 'src/components/userAdmin/UserBulkEditDialog/components/BulkEditDialog';

export const useUserBulkEdit = (
  selectionModel: number[],
  setRequestState: (state: UserAdminRequestState) => void,
) => {
  const {
    isFullSelection,
    searchFilters,
    resetPageNumber,
    setBulkEditDialogOpen,
  } = useUserAdmin();
  const { t } = useTranslation('admin');

  const handleBulkEditSubmit = async (
    values: BulkEditDialogValidatedValues,
  ) => {
    const endpoint = apiPaths.ADMIN.BULK_EDIT;
    const params = isFullSelection
      ? { filter: JSON.stringify(searchFilters) }
      : { id: selectionModel };

    try {
      setRequestState({ status: 'pending' });
      setBulkEditDialogOpen(false);

      const {
        edited_user_count: editedUserCount,
        excluded_role_count: excludedRoleCount,
        excluded_permissions_count: excludedPermissionsCount,
        total_user_count: totalUserCount,
        excluded_users: excludedUsers,
      }: {
        edited_user_count: number;
        excluded_role_count: number;
        excluded_permissions_count: number;
        total_user_count: number;
        excluded_users: [];
      } = await HttpServiceInstance.patch(
        endpoint,
        toSnakeCase(values),
        params,
      ).then((response: any) => response.data);

      const messageLines = [
        t('BulkEdit.EditSuccess', {
          count: editedUserCount,
          totalUserCount: totalUserCount,
        }),
      ];

      if (excludedRoleCount) {
        messageLines.push(
          t('BulkEdit.InfoExcludedRole', {
            count: excludedRoleCount,
          }),
        );
      }

      if (excludedPermissionsCount) {
        messageLines.push(
          t('BulkEdit.InfoExcludedPermissions', {
            count: excludedPermissionsCount,
          }),
        );
      }

      if (excludedUsers) {
        if (excludedUsers.length > 10) {
          // eslint-disable-next-line array-callback-return
          excludedUsers.slice(0, 10).map((item: any) => {
            messageLines.push(
              t('BulkEdit.InfoExcludedRoleUser', {
                email: item,
              }),
            );
          });
        } else {
          excludedUsers.forEach((item: any) => {
            messageLines.push(
              t('BulkEdit.InfoExcludedRoleUser', {
                email: item,
              }),
            );
          });
        }
      }

      resetPageNumber();
      setRequestState({
        status: 'success',
        message: messageLines.join(' '),
      });
    } catch (_error: any) {
      setRequestState({
        status: 'error',
        message: t('BulkEdit.EditError'),
      });
    }
  };

  return {
    handleBulkEditSubmit,
  };
};
